import { useEffect, useState } from 'react';

import { useAppActive } from './useAppActive';

// Live 1s tick. `visible` gates the interval so it only runs while the
// consumer actually needs a live clock (a modal that's open, an
// unfinished workout's elapsed-time counter, ...) — a single shared
// primitive so every "tick every second while X" surface uses the SAME
// setInterval/cleanup instead of re-rolling it per callsite (CN-11).
// Moved here from apps/mobile/src/logger/useNowTick.ts, which was deleted
// (no re-export shim: both consumers import from @heraion/ui).
// Originally extracted verbatim out of LoggerScreen body (Track R); kept
// bit-identical through the move.
// #325: no tick in background; the clock is re-read when the tick restarts.
// `periodMs` (#463): a slower beat for a window that ends now, e.g. the last
// 7 days, which slides while the screen stays open. A beat slower than the
// second reads the clock rounded UP to it: a re-read inside the same period
// is the same number, so the mount re-read redoes no memo.
const readClock = (periodMs: number): number =>
  periodMs > 1000 ? Math.ceil(Date.now() / periodMs) * periodMs : Date.now();

export function useNowTick(visible: boolean, periodMs = 1000): number {
  const [nowMs, setNowMs] = useState(() => readClock(periodMs));
  const active = useAppActive();
  useEffect(() => {
    if (!visible || !active) return undefined;
    setNowMs(readClock(periodMs));
    const id = setInterval(() => setNowMs(readClock(periodMs)), periodMs);
    return () => clearInterval(id);
  }, [visible, active, periodMs]);
  return nowMs;
}
